import { useEffect, useMemo, useState } from 'react'
import {
  subscribeWorkspaceUserDirectory,
  type WorkspaceUserDirectoryEntry,
} from '../services/workspaceUserDirectory'

export interface WorkspaceMemberDirectoryState {
  memberNames: Map<string, string>
  members: WorkspaceUserDirectoryEntry[]
  loaded: boolean
  error: Error | null
}

/**
 * Subscribes to the workspace user directory and returns a uid -> display name map
 * along with the raw members array, a loaded flag, and any subscription error.
 */
export function useWorkspaceMemberNames(workspaceId?: string): WorkspaceMemberDirectoryState {
  const [state, setState] = useState<{
    prevWorkspaceId?: string
    members: WorkspaceUserDirectoryEntry[]
    loaded: boolean
    error: Error | null
  }>({
    prevWorkspaceId: workspaceId,
    members: [],
    loaded: false,
    error: null,
  })

  if (state.prevWorkspaceId !== workspaceId) {
    setState({
      prevWorkspaceId: workspaceId,
      members: [],
      loaded: false,
      error: null,
    })
  }

  useEffect(() => {
    if (!workspaceId) return

    const unsub = subscribeWorkspaceUserDirectory(
      workspaceId,
      (entries) => {
        setState({
          prevWorkspaceId: workspaceId,
          members: entries,
          loaded: true,
          error: null,
        })
      },
      (err) => {
        setState({
          prevWorkspaceId: workspaceId,
          members: [],
          loaded: true,
          error: err,
        })
      },
    )
    return unsub
  }, [workspaceId])

  const memberNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const entry of state.members) {
      map.set(entry.uid, entry.displayName || entry.email || entry.uid)
    }
    return map
  }, [state.members])

  return {
    memberNames,
    members: state.members,
    loaded: !workspaceId ? false : state.loaded,
    error: state.error,
  }
}
